import {
  GenericChildState,
  ParentState,
  getChildState,
  getParentState,
} from "@hopper-b2b/checkout";
import { logger } from "@hopper-b2b/datadog";
import type { History } from "history";
import queryStringParser from "query-string";

import type { DefaultContext, StateValue, send as xstateSend } from "xstate";
import { NubankPaymentState } from "../../checkout/states/payments/types";
import { PATH_HOME } from "../../utils/urlPaths";
import { logContactStateObserved } from "./contactInformationInstrumentation";
import { Event } from "./events";
import { CheckoutQueryParams, type FlightMachineContext } from "./types";

// Tracked states include ParentState enum values plus custom string literal states
type TrackedState = ParentState | "bagsAndSeats";

// List of states that should not be tracked in the query params
const untrackedStates = [
  ParentState.resume,
  ParentState.route,
  ParentState.loading,
  ParentState.passport,
  ParentState.cartQuote,
  ParentState.bagsSelection,
  ParentState.cartUpdate,
  ParentState.cartUpdateBeforeFulfill,
  ParentState.cartFulfill,
];

const untrackedPaymentStates = [
  NubankPaymentState.track,
  NubankPaymentState.requestAuth,
  NubankPaymentState.preAuthCheck,
];

// Map of states that should not transition when query params change
// Map: { state that should not transition => previous tracked state }
// Eg: if user is in "cartQuote" and hits back on their browser, the user should not be allowed to go back 1 step and
// instead stay in the current step till it resolves
const preventTransitionParentStates = {
  [ParentState.cartQuote]: ParentState.passengerInformation,
  [ParentState.cartUpdate]: ParentState.passengerInformation,
  [ParentState.cartUpdateBeforeFulfill]: ParentState.review,
  [ParentState.cartFulfill]: ParentState.review,
};

export const populateCheckoutQueryParams = (
  state: StateValue,
  history: History
) => {
  const queryString = history?.location?.search || "";
  const parsedQuery = queryStringParser.parse(queryString);
  const queryParentState = parsedQuery?.[CheckoutQueryParams.checkoutState];
  const queryChildState = parsedQuery?.[CheckoutQueryParams.checkoutChildState];

  const newParentState = getParentState(state);
  const newChildState = getChildState(state);

  if (
    (newParentState === ParentState.contactInformation &&
      newChildState === GenericChildState.saving) ||
    (queryParentState === ParentState.contactInformation &&
      newParentState !== ParentState.contactInformation)
  ) {
    logContactStateObserved({
      parentState: newParentState,
      childState: newChildState,
    });
  }

  const shouldUpdateQuery = !untrackedStates.includes(
    newParentState as ParentState
  );
  const shouldUpdatePaymentQuery = !untrackedPaymentStates.includes(
    newChildState as NubankPaymentState
  );

  // Log URL update attempts, especially for bagsAndSeats
  if (
    newParentState === "bagsAndSeats" ||
    queryParentState === "bagsAndSeats"
  ) {
    logger().info("Updating URL for bagsAndSeats state", {
      event: "checkout_navigation_url_update_bags_and_seats",
      newParentState,
      queryParentState,
      shouldUpdateQuery,
      url: window.location.href,
      stateValue: JSON.stringify(state),
    });
  }

  // After a successful booking, strip checkout-specific params (cartToken,
  // checkoutState, checkoutChildState) so that stale tokens can't be reused
  // if the user later navigates back to this URL.
  if (newParentState === ParentState.bookingConfirmation) {
    const cleanParams = { ...parsedQuery };
    delete cleanParams[CheckoutQueryParams.checkoutState];
    delete cleanParams[CheckoutQueryParams.checkoutChildState];
    delete cleanParams.cartToken;

    history.replace({
      pathname: history.location.pathname,
      search: queryStringParser.stringify(cleanParams),
      state: undefined,
    });
    return;
  }

  const searchParams = {
    ...parsedQuery,
    [CheckoutQueryParams.checkoutState]: newParentState,
  };

  // Handle updating the urls only when we're in the payment state
  if (newParentState === ParentState.payment) {
    searchParams[CheckoutQueryParams.checkoutChildState] = newChildState;
  } else {
    if (parsedQuery?.[CheckoutQueryParams.checkoutChildState]) {
      searchParams[CheckoutQueryParams.checkoutChildState] = undefined;
    }
  }
  const historyData = {
    pathname: history.location.pathname,
    search: queryStringParser.stringify(searchParams),
    state: history.location.state,
  };

  // CRITICAL: Log when transitioning FROM bagsAndSeats to another state
  // This is when the bug occurs - user clicks continue and should go to review/cartUpdate
  // ref https://hopper-jira.atlassian.net/browse/ATL-300
  if (
    queryParentState === "bagsAndSeats" &&
    newParentState !== "bagsAndSeats"
  ) {
    logger().info("CRITICAL: Transitioning FROM bagsAndSeats state", {
      event: "checkout_navigation_leaving_bags_and_seats",
      fromState: queryParentState,
      toState: newParentState,
      url: window.location.href,
      newUrl: `${historyData.pathname}${historyData.search}`,
      stateValue: JSON.stringify(state),
      shouldUpdateQuery,
      isTransitioningToCartUpdate: newParentState === ParentState.cartUpdate,
      isTransitioningToReview: newParentState === ParentState.review,
      isTransitioningToPassengerInfo:
        newParentState === ParentState.passengerInformation,
    });
  }

  //  If xstate state value is not in query params or if it conflicts with what the
  //  current query params contains, then we push new query params to the URL
  if (!queryParentState && shouldUpdateQuery) {
    logger().info("Replacing URL - no existing query state", {
      event: "checkout_navigation_url_replace",
      newParentState,
      url: window.location.href,
      newUrl: `${historyData.pathname}${historyData.search}`,
    });
    history.replace(historyData);
  } else if (queryParentState && shouldUpdateQuery) {
    if (newParentState !== queryParentState) {
      // If the parent states don't match
      if (
        newParentState !== ParentState.payment ||
        (newParentState === ParentState.payment &&
          shouldUpdatePaymentQuery &&
          newChildState !== queryChildState)
      ) {
        logger().info("Pushing new URL - state mismatch", {
          event: "checkout_navigation_url_push",
          fromState: queryParentState,
          toState: newParentState,
          url: window.location.href,
          newUrl: `${historyData.pathname}${historyData.search}`,
          isBagsAndSeatsTransition:
            newParentState === "bagsAndSeats" ||
            queryParentState === "bagsAndSeats",
        });
        history.push(historyData);
      }
    } else if (
      newParentState === ParentState.payment &&
      shouldUpdatePaymentQuery &&
      newChildState !== queryChildState
    ) {
      // If the parent states match but if they're payment, only update if the child states don't match
      logger().info("Pushing URL - payment child state change", {
        event: "checkout_navigation_url_push_payment_child",
        newParentState,
        fromChildState: queryChildState,
        toChildState: newChildState,
        url: window.location.href,
      });
      history.push(historyData);
    }
  }
};

export const mapFlightStateToParentTransitionEvent: Partial<
  Record<TrackedState, Event>
> = {
  [ParentState.contactInformation]: Event.GO_TO_CONTACT_INFORMATION,
  [ParentState.passengerInformation]: Event.GO_TO_PASSENGER_SELECT,
  [ParentState.review]: Event.GO_TO_REVIEW,
  [ParentState.bookingConfirmation]: Event.GO_TO_REVIEW,
  [ParentState.seatSelection]: Event.GO_TO_SEAT_SELECTION,
  bagsAndSeats: Event.GO_TO_BAGS_AND_SEATS_SELECTION,
};

export const mapPaymentStateToTransitionEvent = {
  [NubankPaymentState.selectCreditOrDebit]:
    Event.GO_TO_PAYMENT_SELECT_CREDIT_OR_DEBIT,
  [NubankPaymentState.credit]: Event.GO_TO_PAYMENT_SELECT_CREDIT,
  [NubankPaymentState.review]: Event.GO_TO_PAYMENT_SELECT_REVIEW,
};

export const transitionStateOnPathnameChange = (
  state: StateValue,
  history: History,
  send: typeof xstateSend
) => {
  const queryString = history.location.search;
  const parsedQuery = queryStringParser.parse(queryString);
  const queryParentState = parsedQuery?.[CheckoutQueryParams.checkoutState];
  const queryChildState = parsedQuery?.[CheckoutQueryParams.checkoutChildState];

  const currentParentState = getParentState(state);
  const currentChildState = getChildState(state);

  // Log navigation state for debugging
  logger().info("Navigation state sync check", {
    event: "checkout_navigation_state_sync",
    currentParentState,
    currentChildState,
    queryParentState,
    queryChildState,
    url: window.location.href,
    stateValue: JSON.stringify(state),
  });

  // If current state is untracked, don't sync URL to state machine
  if (untrackedStates.includes(currentParentState as ParentState)) {
    logger().info("Skipping URL sync - current state is untracked", {
      event: "checkout_navigation_skip_untracked",
      currentParentState,
      queryParentState,
      url: window.location.href,
    });
    return;
  }

  const preventParentStateTransition =
    !!preventTransitionParentStates[currentParentState];

  const allowParentStateTransition =
    !preventParentStateTransition &&
    queryParentState &&
    !untrackedStates.includes(queryParentState as ParentState);

  const hasSucceededFulfillment =
    currentParentState === ParentState.bookingConfirmation;

  // If the query and checkout state are in payment, check child states
  if (
    queryParentState === ParentState.payment &&
    currentParentState === ParentState.payment
  ) {
    if (
      queryChildState &&
      queryChildState !== currentChildState &&
      !untrackedPaymentStates.includes(queryChildState as NubankPaymentState)
    ) {
      logger().info("Syncing payment child state", {
        event: "checkout_navigation_payment_child_sync",
        currentChildState,
        queryChildState,
        url: window.location.href,
      });
      send({
        type: mapPaymentStateToTransitionEvent[
          queryChildState as NubankPaymentState
        ],
      });
    }
  } else if (
    queryParentState &&
    currentParentState !== queryParentState &&
    allowParentStateTransition
  ) {
    if (hasSucceededFulfillment) {
      handleFulfillSuccess(history);
    } else {
      const transitionEvent =
        mapFlightStateToParentTransitionEvent[queryParentState as TrackedState];
      if (transitionEvent) {
        logger().info("Transitioning state based on URL change", {
          event: "checkout_navigation_state_transition",
          fromState: currentParentState,
          toState: queryParentState,
          transitionEvent,
          preventParentStateTransition,
          allowParentStateTransition,
          url: window.location.href,
          stateValue: JSON.stringify(state),
          // Special logging for bagsAndSeats navigation
          isBagsAndSeatsNavigation:
            queryParentState === "bagsAndSeats" ||
            currentParentState === "bagsAndSeats",
          // CRITICAL: Log when navigating TO bagsAndSeats from URL sync
          // This could indicate browser back button or URL manipulation
          isNavigatingToBagsAndSeats: queryParentState === "bagsAndSeats",
          isNavigatingFromBagsAndSeats: currentParentState === "bagsAndSeats",
        });
        send({
          type: transitionEvent,
        });
      } else {
        logger().info("No transition event found for state", {
          event: "checkout_navigation_no_transition_event",
          currentParentState,
          queryParentState,
          url: window.location.href,
        });
      }
    }
  } else if (
    preventParentStateTransition &&
    preventTransitionParentStates[currentParentState] !== queryParentState
  ) {
    logger().info("Preventing state transition", {
      event: "checkout_navigation_prevent_transition",
      currentParentState,
      queryParentState,
      preventTransitionState: preventTransitionParentStates[currentParentState],
      url: window.location.href,
    });
    const historyData = {
      pathname: history.location.pathname,
      search: queryStringParser.stringify({
        ...parsedQuery,
        [CheckoutQueryParams.checkoutState]:
          preventTransitionParentStates[currentParentState],
      }),
      state: history.location.state,
    };
    // Pushing the same stack that was removed when the user goes 1 step back in their browser
    history.push(historyData);
  }
};

export const handleFulfillSuccess = (history: History) => {
  // Send user to the home page
  history.replace(PATH_HOME);
};

export const validateContext = (ctx: FlightMachineContext | DefaultContext) =>
  (ctx.flightShop.shopPricingInfo.fare?.length || -1) > 0 &&
  ctx.flightSearch.departureDate &&
  ctx.flightSearch.destination &&
  ctx.flightSearch.origin &&
  ctx.flightShop.selectedTrip.outgoingFareId &&
  ctx.flightShop.selectedTrip.outgoingSliceId &&
  ctx.flightShop.selectedTrip.tripId &&
  Object.keys(ctx.flightShop.airports || {}).length > 0;

export const getCheckoutStepOnQueryParam = () => {
  const queryString = window.location.search;
  const parsedQuery = new URLSearchParams(queryString);
  return parsedQuery.get(CheckoutQueryParams.checkoutState);
};
